import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const HUB_LINKS = [
  { to: '/gifts/gifts-for-him', label: 'Gifts for Him' },
  { to: '/gifts/gifts-for-her', label: 'Gifts for Her' },
  { to: '/gifts/gifts-for-mom', label: 'Gifts for Mom' },
  { to: '/gifts/gifts-for-dad', label: 'Gifts for Dad' },
  { to: '/gifts/birthday-gifts', label: 'Birthday Gifts' },
  { to: '/gifts/christmas-gifts', label: 'Christmas Gifts' },
  { to: '/gifts/anniversary-gifts', label: 'Anniversary Gifts' },
  { to: '/gifts/wedding-gifts', label: 'Wedding Gifts' },
  { to: '/gifts/graduation-gifts', label: 'Graduation Gifts' },
  { to: '/gifts/gifts-under-50', label: 'Gifts Under $50' },
];

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [giftDropdownOpen, setGiftDropdownOpen] = useState(false);
  const [mobileGiftIdeasOpen, setMobileGiftIdeasOpen] = useState(false);
  const dropdownRef = useRef(null);
  const timeoutRef = useRef(null);
  const navigate = useNavigate();

  const handleFindGiftsClick = () => {
    localStorage.removeItem('pickify_quiz_progress');
    localStorage.removeItem('pickify_quiz_complete');
    navigate('/');
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setGiftDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDropdownEnter = () => {
    clearTimeout(timeoutRef.current);
    setGiftDropdownOpen(true);
  };

  const handleDropdownLeave = () => {
    timeoutRef.current = setTimeout(() => setGiftDropdownOpen(false), 200);
  };

  const handleDropdownClick = () => {
    // On touch devices, hover doesn't work — use click as fallback
    setGiftDropdownOpen(prev => !prev);
  };

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link
            to="/"
            onClick={handleFindGiftsClick}
            className="flex items-center gap-2"
          >
            <picture>
              <source srcSet="/logo-icon.webp" type="image/webp" />
              <img
                src="/logo-icon.png"
                alt="Pickify AI - Gift Finder"
                className="h-10 w-auto"
                width="40"
                height="40"
              />
            </picture>
            <span className="text-xl font-bold text-gray-800">
              Pickify<span className="text-orange-500">AI</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <Link
              to="/"
              onClick={handleFindGiftsClick}
              className="text-gray-700 hover:text-orange-500 transition-colors font-medium"
            >
              Home
            </Link>
            <Link
              to="/"
              onClick={handleFindGiftsClick}
              className="text-gray-700 hover:text-orange-500 transition-colors font-medium"
            >
              Find Gifts
            </Link>

            {/* Gift Ideas Dropdown */}
            <div
              ref={dropdownRef}
              className="relative"
              onMouseEnter={handleDropdownEnter}
              onMouseLeave={handleDropdownLeave}
            >
              <button
                onClick={handleDropdownClick}
                className="text-gray-700 hover:text-orange-500 transition-colors font-medium flex items-center gap-1"
                aria-expanded={giftDropdownOpen}
                aria-haspopup="true"
              >
                Gift Ideas
                <svg className={`w-4 h-4 transition-transform ${giftDropdownOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {giftDropdownOpen && (
                <div
                  className="absolute top-full left-1/2 -translate-x-1/2 pt-2 w-56 z-50"
                  onMouseEnter={handleDropdownEnter}
                  onMouseLeave={handleDropdownLeave}
                >
                  <div className="bg-white rounded-lg shadow-lg border border-gray-200 py-2">
                    {HUB_LINKS.map((hub) => (
                      <Link
                        key={hub.to}
                        to={hub.to}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors"
                        onClick={() => setGiftDropdownOpen(false)}
                      >
                        {hub.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/deals"
              className="text-gray-700 hover:text-orange-500 transition-colors font-medium"
            >
              Deals
            </Link>
            <Link
              to="/blog"
              className="text-gray-700 hover:text-orange-500 transition-colors font-medium"
            >
              Blog
            </Link>
          </nav>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 rounded-md text-gray-700 hover:text-orange-500 hover:bg-gray-100"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              {mobileMenuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200">
            <div className="flex flex-col space-y-4">
              <Link
                to="/"
                className="text-gray-700 hover:text-orange-500 transition-colors font-medium"
                onClick={() => {
                  setMobileMenuOpen(false);
                  handleFindGiftsClick();
                }}
              >
                Home
              </Link>
              <Link
                to="/"
                className="text-gray-700 hover:text-orange-500 transition-colors font-medium"
                onClick={() => {
                  setMobileMenuOpen(false);
                  handleFindGiftsClick();
                }}
              >
                Find Gifts
              </Link>

              {/* Mobile Gift Ideas expandable */}
              <div>
                <button
                  onClick={() => setMobileGiftIdeasOpen(!mobileGiftIdeasOpen)}
                  className="text-gray-700 hover:text-orange-500 transition-colors font-medium flex items-center gap-1 w-full"
                  aria-expanded={mobileGiftIdeasOpen}
                >
                  Gift Ideas
                  <svg className={`w-4 h-4 transition-transform ${mobileGiftIdeasOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {mobileGiftIdeasOpen && (
                  <div className="ml-4 mt-2 flex flex-col space-y-2">
                    {HUB_LINKS.map((hub) => (
                      <Link
                        key={hub.to}
                        to={hub.to}
                        className="text-sm text-gray-600 hover:text-orange-500 transition-colors"
                        onClick={() => {
                          setMobileMenuOpen(false);
                          setMobileGiftIdeasOpen(false);
                        }}
                      >
                        {hub.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <Link
                to="/deals"
                className="text-gray-700 hover:text-orange-500 transition-colors font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                Deals
              </Link>
              <Link
                to="/blog"
                className="text-gray-700 hover:text-orange-500 transition-colors font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                Blog
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
